import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Download, FileSignature, X } from 'lucide-react';
import { Botao, Selo, type CorSelo } from '../../../web/src/componentes/base';
import { Estado, Vazio } from '../../../web/src/componentes/estados';
import { Entrada, Rotulo } from '../../../web/src/componentes/formulario';
import { Modal } from '../../../web/src/componentes/sobreposicao';
import { useToast } from '../../../web/src/componentes/toast';
import { mensagemDeErro } from '../../../web/src/nucleo/api';
import { textoLivre } from '../../../web/src/nucleo/formato';
import { baixar, pget, ppost } from '../api';
import { useSessao } from '../sessao';
import { dataCurta, tamanhoArquivo } from './comum';

/** Documento para assinar, como a API do portal devolve (`id` é o do signatário: um por acesso). */
export interface AssinaturaPortal {
  id: string;
  assinatura_id: string;
  documento_nome: string;
  versao: number;
  sha256_documento: string | null;
  mensagem: string | null;
  prazo: string | null;
  enviada_em: string | null;
  estado: 'pendente' | 'assinado' | 'recusado';
  assinatura_estado: 'enviado' | 'assinado' | 'recusado' | 'cancelado';
  decidido_em: string | null;
  arquivo_nome: string;
  mime: string;
  tamanho: number;
  pode_decidir: boolean;
}

/** Situação que o cliente lê: a decisão dele; pendente de envio cancelado ou vencido vira "encerrado". */
export function situacaoDoCliente(a: Pick<AssinaturaPortal, 'estado' | 'assinatura_estado' | 'pode_decidir'>): { chave: 'pendente' | 'assinado' | 'recusado' | 'encerrado'; cor: CorSelo } {
  if (a.estado === 'assinado') return { chave: 'assinado', cor: 'verde' };
  if (a.estado === 'recusado') return { chave: 'recusado', cor: 'perigo' };
  return a.pode_decidir ? { chave: 'pendente', cor: 'laranja' } : { chave: 'encerrado', cor: 'neutro' };
}

const classeTexto =
  'w-full min-w-0 rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco';

/**
 * Para assinar (Pacote 2): os documentos que a Malgo enviou para o cliente assinar. Ler (baixar o arquivo, que fica na
 * trilha), assinar com o código novo do e-mail (a prova recente da sessão do portal) ou recusar com o motivo.
 */
export function ParaAssinar() {
  const { t, i18n } = useTranslation('portal');
  const idioma = i18n.language.slice(0, 2);
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [assinando, setAssinando] = useState<AssinaturaPortal | null>(null);
  const [recusando, setRecusando] = useState<AssinaturaPortal | null>(null);
  const [codigo, setCodigo] = useState('');
  const [motivo, setMotivo] = useState('');
  const [enviandoCodigo, setEnviandoCodigo] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const q = useQuery({ queryKey: ['portal', 'assinaturas'], queryFn: () => pget<{ itens: AssinaturaPortal[]; disponivel: boolean }>('/assinaturas') });
  const fechar = () => {
    setAssinando(null);
    setRecusando(null);
    setCodigo('');
    setMotivo('');
  };
  const atualizar = async () => {
    await qc.invalidateQueries({ queryKey: ['portal', 'assinaturas'] });
    await qc.invalidateQueries({ queryKey: ['portal', 'hoje'] });
  };
  const decidir = async (a: AssinaturaPortal, acao: 'assinar' | 'recusar') => {
    setSalvando(true);
    try {
      await ppost(`/assinaturas/${a.id}/${acao}`, acao === 'assinar' ? { codigo } : { motivo });
      toast.sucesso(acao === 'assinar' ? t('assinar.assinado') : t('assinar.recusado'));
      fechar();
      await atualizar();
    } catch (e) {
      toast.erro(mensagemDeErro(e));
    } finally {
      setSalvando(false);
    }
  };
  return (
    <>
      <Estado consulta={q}>
        {(d) =>
          d.itens.length ? (
            <ul className="divide-y divide-borda rounded-xl border border-borda bg-sup-1" data-assinaturas>
              {d.itens.map((a, _i, todos) => {
                // Um só primário à vista (r23 visual M1): o "Assinar" do primeiro pendente; os outros ficam secundários.
                const primaria = todos.find((x) => x.pode_decidir)?.id === a.id;
                const sit = situacaoDoCliente(a);
                const nome = textoLivre(a.documento_nome, s.empresa.nome);
                return (
                  <li key={a.id} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center">
                    <div className="min-w-0 flex-1">
                      <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                        <span className="min-w-0 break-words">{nome}</span>
                        <span className="min-w-min">
                          <Selo cor={sit.cor}>{t(`assinar.situacao.${sit.chave}`)}</Selo>
                        </span>
                      </p>
                      <p className="text-xs text-tinta-3">
                        {t('assinar.versao', { versao: a.versao })} · {tamanhoArquivo(a.tamanho)}
                        {a.enviada_em ? ` · ${t('assinar.enviado', { data: dataCurta(a.enviada_em, idioma) })}` : ''}
                        {a.prazo && a.pode_decidir ? ` · ${t('assinar.ate', { data: dataCurta(a.prazo, idioma) })}` : ''}
                        {a.decidido_em ? ` · ${dataCurta(a.decidido_em, idioma)}` : ''}
                      </p>
                      {a.mensagem && a.pode_decidir && <p className="mt-1 text-sm break-words text-tinta-2">{textoLivre(a.mensagem, s.empresa.nome)}</p>}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Botao
                        tamanho="sm"
                        icone={<Download className="size-4" aria-hidden />}
                        aria-label={`${t('assinar.ler')}: ${nome}`}
                        onClick={async () => {
                          try {
                            await baixar(`/assinaturas/${a.id}/arquivo`, a.arquivo_nome);
                          } catch (e) {
                            toast.erro(mensagemDeErro(e));
                          }
                        }}
                      >
                        {t('assinar.ler')}
                      </Botao>
                      {a.pode_decidir && (
                        <>
                          <Botao tamanho="sm" icone={<X className="size-4" aria-hidden />} aria-label={`${t('assinar.recusar')}: ${nome}`} onClick={() => setRecusando(a)}>
                            {t('assinar.recusar')}
                          </Botao>
                          <Botao tamanho="sm" variante={primaria ? 'primario' : 'secundario'} icone={<FileSignature className="size-4" aria-hidden />} aria-label={`${t('assinar.assinar')}: ${nome}`} onClick={() => setAssinando(a)}>
                            {t('assinar.assinar')}
                          </Botao>
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="rounded-xl border border-borda bg-sup-1">
              <Vazio texto={t('assinar.vazio')} />
            </div>
          )
        }
      </Estado>
      <Modal
        aberto={!!assinando}
        aoFechar={fechar}
        titulo={t('assinar.titulo')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={fechar}>
              {t('assinar.cancelar')}
            </Botao>
            <Botao variante="primario" carregando={salvando} aria-disabled={codigo.length !== 6} onClick={() => assinando && codigo.length === 6 && decidir(assinando, 'assinar')}>
              {t('assinar.assinar')}
            </Botao>
          </>
        }
      >
        {assinando && (
          <div className="flex flex-col gap-3">
            <p className="text-sm font-medium break-words text-tinta-1">{textoLivre(assinando.documento_nome, s.empresa.nome)}</p>
            <p className="text-sm text-tinta-2">{t('assinar.explicacao')}</p>
            <p className="text-xs break-all text-tinta-3">
              {t('assinar.hash')}: <span className="font-mono">{assinando.sha256_documento}</span>
            </p>
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-0 flex-1">
                <Rotulo id="assinar-codigo" rotulo={t('assinar.codigo')} ajuda={t('assinar.codigoAjuda')}>
                  <Entrada id="assinar-codigo" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={codigo} onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ''))} />
                </Rotulo>
              </div>
              <Botao
                carregando={enviandoCodigo}
                onClick={async () => {
                  setEnviandoCodigo(true);
                  try {
                    await ppost('/conta/codigo');
                    toast.sucesso(t('perfil.codigoEnviado'));
                  } catch (e) {
                    toast.erro(mensagemDeErro(e));
                  } finally {
                    setEnviandoCodigo(false);
                  }
                }}
              >
                {t('perfil.enviarCodigo')}
              </Botao>
            </div>
          </div>
        )}
      </Modal>
      <Modal
        aberto={!!recusando}
        aoFechar={fechar}
        titulo={t('assinar.recusarTitulo')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={fechar}>
              {t('assinar.cancelar')}
            </Botao>
            <Botao variante="primario" carregando={salvando} aria-disabled={!motivo.trim()} onClick={() => recusando && motivo.trim() && decidir(recusando, 'recusar')}>
              {t('assinar.recusar')}
            </Botao>
          </>
        }
      >
        <Rotulo id="recusar-motivo" rotulo={t('assinar.motivo')} obrigatorio ajuda={t('assinar.motivoAjuda')}>
          <textarea id="recusar-motivo" rows={3} maxLength={500} value={motivo} onChange={(e) => setMotivo(e.target.value)} className={classeTexto} />
        </Rotulo>
      </Modal>
    </>
  );
}
